/**
 * The library link on a course component: what it is, and what to do about an update.
 *
 * A linked component is the payoff of having a library, and it is also the part most likely to
 * confuse. Three facts have to be legible without clicking anything:
 *
 *   1. This content came from a library and is still connected to it.
 *   2. The library has moved on, or it has not.
 *   3. This copy has local edits, or it has not.
 *
 * Fact 3 is why the update is a choice rather than a button. Syncing can either keep local
 * edits or replace them, and the platform will do whichever it is told — so both options are
 * offered by name, with the safe one first. A single "Update" button here would silently throw
 * away someone's rewrite, and they would not find out until a learner read it.
 *
 * Skipping is offered too, and it is not the same as ignoring: a declined version is recorded,
 * so the prompt stays gone until the library publishes something newer.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge, Banner, Button, ConfirmDialog, Icon } from '../../shared/ui';
import {
  assetWarnings,
  declineSync,
  syncFromUpstream,
  unlinkFromUpstream,
  type StaticFileNotices,
} from '../../shared/lib/library-link';
import './linked-block.css';

/** The subset of the link the children endpoint already gives us — no extra request needed. */
export interface LinkState {
  upstream_ref: string | null;
  ready_to_sync: boolean;
  version_synced?: number | null;
  version_available?: number | null;
  downstream_customized?: string[];
  error_message?: string | null;
}

/** Field names are internal; these are the ones an author would recognise. */
const FIELD_LABELS: Record<string, string> = {
  display_name: 'the title',
  data: 'the content',
  max_attempts: 'the number of attempts',
  weight: 'the weight',
};

function describeCustomised(fields: string[]): string {
  const named = fields.map((field) => FIELD_LABELS[field] ?? field);
  if (named.length === 1) return `You have changed ${named[0]} in this course.`;
  const last = named.pop();
  return `You have changed ${named.join(', ')} and ${last} in this course.`;
}

export function LinkedBlock({
  blockKey,
  link,
  onChanged,
}: {
  blockKey: string;
  link: LinkState;
  /** Refetch after anything that alters the block or its link. */
  onChanged: () => void;
}) {
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [notices, setNotices] = useState<StaticFileNotices | undefined>();

  const sync = useMutation({
    mutationFn: (overrideCustomizations: boolean) =>
      syncFromUpstream(blockKey, { overrideCustomizations }),
    onSuccess: (result) => {
      setNotices(result.static_file_notices);
      onChanged();
    },
  });

  const skip = useMutation({
    mutationFn: () => declineSync(blockKey),
    onSuccess: onChanged,
  });

  const unlink = useMutation({
    mutationFn: () => unlinkFromUpstream(blockKey),
    onSuccess: () => {
      setConfirmUnlink(false);
      onChanged();
    },
  });

  if (!link.upstream_ref) return null;

  const customised = link.downstream_customized ?? [];
  const busy = sync.isPending || skip.isPending || unlink.isPending;
  const warnings = assetWarnings(notices);

  // A broken link is worth saying out loud: the library block may have been deleted, and the
  // course content is now frozen with no way to update it.
  if (link.error_message) {
    return (
      <div className="linked" data-tone="broken">
        <Icon name="link_off" />
        <div className="linked__body">
          <strong>This link to a library is broken.</strong>
          <p>
            {link.error_message} The content itself is unaffected and learners still see it, but it
            can no longer receive updates.
          </p>
        </div>
        <Button variant="ghost" busy={unlink.isPending} onClick={() => setConfirmUnlink(true)}>
          Unlink
        </Button>
        {confirmUnlink && (
          <UnlinkConfirm
            busy={unlink.isPending}
            onConfirm={() => unlink.mutate()}
            onClose={() => setConfirmUnlink(false)}
          />
        )}
      </div>
    );
  }

  if (!link.ready_to_sync) {
    return (
      <div className="linked" data-tone="quiet">
        <Icon name="link" />
        <div className="linked__body">
          <span>
            From a library, up to date
            {customised.length > 0 && ' — with your own changes on top'}
          </span>
        </div>
        <button
          type="button"
          className="linked__quiet-action"
          disabled={busy}
          onClick={() => setConfirmUnlink(true)}
        >
          Unlink
        </button>
        {confirmUnlink && (
          <UnlinkConfirm
            busy={unlink.isPending}
            onConfirm={() => unlink.mutate()}
            onClose={() => setConfirmUnlink(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="linked" data-tone="update">
      <Icon name="sync" />
      <div className="linked__body">
        <strong>The library has a newer version of this.</strong>
        {customised.length > 0 ? (
          <p>
            {describeCustomised(customised)} Choose whether to keep those changes or replace them
            with the library&rsquo;s version.
          </p>
        ) : (
          <p>Taking the update replaces this copy with the library&rsquo;s current version.</p>
        )}

        {sync.isError && (
          <Banner tone="danger">
            <strong>The update was not applied.</strong> This component is unchanged. The library
            block may have been removed or may not support syncing.
          </Banner>
        )}

        {warnings.length > 0 && (
          <Banner tone="warning">
            <strong>Updated, with notes about its files.</strong>
            <ul>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Banner>
        )}

        <div className="linked__actions">
          {customised.length > 0 ? (
            <>
              {/* Safe option first and visually primary. Keeping local work is the recoverable
                  choice: an author who keeps edits can always take the library version later,
                  whereas overwriting is gone. */}
              <Button variant="primary" busy={sync.isPending} onClick={() => sync.mutate(false)}>
                Update, keep my changes
              </Button>
              <Button busy={sync.isPending} onClick={() => sync.mutate(true)}>
                Use the library version
              </Button>
            </>
          ) : (
            <Button variant="primary" busy={sync.isPending} onClick={() => sync.mutate(false)}>
              <Icon name="sync" />
              Take the update
            </Button>
          )}
          <Button variant="ghost" busy={skip.isPending} onClick={() => skip.mutate()}>
            Skip this version
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => setConfirmUnlink(true)}>
            Unlink
          </Button>
        </div>

        {link.version_synced != null && link.version_available != null && (
          <p className="linked__versions">
            <Badge tone="neutral">v{link.version_synced} here</Badge>
            <Icon name="arrow_forward" />
            <Badge tone="info">v{link.version_available} in the library</Badge>
          </p>
        )}
      </div>

      {confirmUnlink && (
        <UnlinkConfirm
          busy={unlink.isPending}
          onConfirm={() => unlink.mutate()}
          onClose={() => setConfirmUnlink(false)}
        />
      )}
    </div>
  );
}

function UnlinkConfirm({
  busy,
  onConfirm,
  onClose,
}: {
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmDialog
      title="Unlink from the library?"
      confirmLabel="Unlink"
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
      body={
        <>
          <p>
            The content stays exactly as it is and learners see no change. It simply stops
            tracking the library, so future library updates will not reach it.
          </p>
          <p>
            Relinking it is not possible from this screen — you would remove it and add it from
            the library again.
          </p>
        </>
      }
    />
  );
}
